/**
 * shared/components/ViewToggle.jsx
 * --------------------------------
 * Tiny [Cards | Table] pill toggle. Persists user pref in localStorage.
 *
 *   const [view, setView] = useViewToggle("admin-listings", "cards");
 *   <ViewToggle value={view} onChange={setView} testidPrefix="al" />
 *
 * v2.71.53 — Initial. Drop-in for any admin/dealer/builder list that
 * needs to flip between card and table modes without losing power-user
 * features.
 */

import React from "react";
import { LayoutGrid, Rows } from "lucide-react";

export function useViewToggle(key, defaultView = "cards") {
  const storageKey = `he-view-${key}`;
  const [view, setView] = React.useState(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v === "cards" || v === "table" ? v : defaultView;
    } catch { return defaultView; }
  });
  const update = React.useCallback((v) => {
    setView(v);
    try { localStorage.setItem(storageKey, v); } catch {}
  }, [storageKey]);
  return [view, update];
}

export default function ViewToggle({ value, onChange, testidPrefix = "view" }) {
  return (
    <div
      data-testid={`${testidPrefix}-toggle`}
      className="inline-flex items-center bg-slate-100 border border-slate-200 rounded-md p-0.5"
      role="tablist"
      aria-label="Switch view"
    >
      <button
        data-testid={`${testidPrefix}-toggle-cards`}
        onClick={() => onChange("cards")}
        role="tab"
        aria-selected={value === "cards"}
        className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-[5px] text-[11.5px] font-semibold transition-all ${
          value === "cards"
            ? "bg-white text-indigo-700 shadow-sm"
            : "text-slate-600 hover:text-slate-900"
        }`}
      >
        <LayoutGrid size={12} /> Cards
      </button>
      <button
        data-testid={`${testidPrefix}-toggle-table`}
        onClick={() => onChange("table")}
        role="tab"
        aria-selected={value === "table"}
        className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-[5px] text-[11.5px] font-semibold transition-all ${
          value === "table"
            ? "bg-white text-indigo-700 shadow-sm"
            : "text-slate-600 hover:text-slate-900"
        }`}
      >
        <Rows size={12} /> Table
      </button>
    </div>
  );
}
